export type PublicInvoiceStatus = 'payment_due' | 'waiting_for_completion' | 'paid' | 'finance_review' | 'voided';

export interface PublicInvoiceView {
  number: string;
  status: PublicInvoiceStatus;
  currency: 'BBD';
  seller: { name: string; address?: string; email?: string; tin?: string };
  customer: { name: string; company?: string; billing_address?: string; email?: string };
  terms: string;
  issued_at: string;
  due_date: string | null;
  subtotal: string;
  invoice_discount: string;
  vat: string;
  total: string;
  adjustments: Array<{ kind: string; total_delta: string }>;
  paid: string;
  balance: string;
  amount_due_now: string;
  can_pay: boolean;
  mock_mode?: boolean;
  lines: Array<{ description: string; quantity: string; unit_price: string; subtotal: string }>;
}

export interface PublicPaymentIntent {
  intent_id: string;
  installment: 'full' | 'deposit' | 'balance';
  amount: string;
  currency: 'BBD';
  provider_url: string;
  expires_at: string;
  reused: boolean;
  mock_mode?: boolean;
}

export class PublicInvoiceRequestError extends Error {
  constructor(message: string, public readonly status: number, public readonly code: string | null) {
    super(message);
    this.name = 'PublicInvoiceRequestError';
  }
}

async function request<T>(path: string, method: 'GET' | 'POST'): Promise<T> {
  const response = await fetch(path, { method, cache: 'no-store', credentials: 'omit', headers: method === 'POST' ? { 'Content-Type': 'application/json' } : undefined });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new PublicInvoiceRequestError(
      typeof body.message === 'string' ? body.message : 'This invoice link is unavailable. Please contact Made.bb.',
      response.status,
      typeof body.error === 'string' ? body.error : null,
    );
  }
  return response.json() as Promise<T>;
}

export const publicInvoicePaymentService = {
  get(token: string): Promise<PublicInvoiceView> {
    return request(`/api/invoice-pay/${encodeURIComponent(token)}`, 'GET');
  },
  createIntent(token: string): Promise<PublicPaymentIntent> {
    // The browser never supplies amount, currency or installment.
    return request(`/api/invoice-pay/${encodeURIComponent(token)}/intents`, 'POST');
  },
  completeMockPayment(token: string, intentId: string): Promise<{ status: string; invoice_id: string }> {
    return request(`/api/invoice-pay/${encodeURIComponent(token)}/mock-completions/${encodeURIComponent(intentId)}`, 'POST');
  },
};
